import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiGetPaged, apiSend, apiUpload, apiDownload } from '../lib/api';
import type {
  AdminUser,
  AuditLogEntry,
  Assignment,
  Booking,
  DuplicatePolicy,
  FunnelSummary,
  ImportBatch,
  ImportCommitResult,
  ImportRow,
  ImportUploadResult,
  Overview,
  Participation,
  Product,
  ProductImage,
  QrRack,
  Rack,
  Stall,
  Visitor,
  VisitorDetail,
} from '../lib/types';

type Query = Record<string, unknown>;
const clean = (q: Query) => Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== ''));

// ── Dashboard / analytics ──
export const useOverview = () =>
  useQuery({ queryKey: ['overview'], queryFn: () => apiGet<Overview>('/analytics/overview') });

export const useParticipationAnalytics = (id: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['analytics', 'participation', id, params],
    enabled: !!id,
    queryFn: () => apiGet<any>(`/analytics/participations/${id}`, clean(params)),
  });

export const useStallBreakdown = (id: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['analytics', 'stalls', id, params],
    enabled: !!id,
    queryFn: () => apiGet<any[]>(`/analytics/participations/${id}/stalls`, clean(params)),
  });

export const useRackBreakdown = (id: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['analytics', 'racks', id, params],
    enabled: !!id,
    queryFn: () => apiGet<any[]>(`/analytics/participations/${id}/racks`, clean(params)),
  });

export const useProductBreakdown = (id: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['analytics', 'products', id, params],
    enabled: !!id,
    queryFn: () => apiGet<any>(`/analytics/participations/${id}/products`, clean(params)),
  });

export type { FunnelSummary };

// ── Participations ──
export const useParticipations = (params: Query = {}) =>
  useQuery({
    queryKey: ['participations', params],
    queryFn: () => apiGetPaged<Participation[]>('/participations', clean({ limit: 100, ...params })),
  });

export const useParticipation = (id: string | undefined) =>
  useQuery({
    queryKey: ['participation', id],
    enabled: !!id,
    queryFn: () => apiGet<Participation>(`/participations/${id}`),
  });

export function useParticipationMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['participations'] });
  return {
    create: useMutation({ mutationFn: (b: Partial<Participation>) => apiSend<Participation>('post', '/participations', b), onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Partial<Participation> }) =>
        apiSend<Participation>('put', `/participations/${id}`, body),
      onSuccess: (_d, v) => {
        inv();
        qc.invalidateQueries({ queryKey: ['participation', v.id] });
      },
    }),
    remove: useMutation({
      mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
        apiSend('delete', `/participations/${id}`, undefined, { params: force ? { force: true } : {} }),
      onSuccess: inv,
    }),
  };
}

// ── Stalls ──
export const useStalls = (participationId: string | undefined) =>
  useQuery({
    queryKey: ['stalls', participationId],
    enabled: !!participationId,
    queryFn: () => apiGetPaged<Stall[]>(`/participations/${participationId}/stalls`, { limit: 100 }),
  });

export function useStallMutations(participationId?: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['stalls', participationId] });
  return {
    create: useMutation({ mutationFn: (b: Partial<Stall>) => apiSend<Stall>('post', `/participations/${participationId}/stalls`, b), onSuccess: inv }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Stall> }) => apiSend<Stall>('put', `/stalls/${id}`, body), onSuccess: inv }),
    remove: useMutation({
      mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
        apiSend('delete', `/stalls/${id}`, undefined, { params: force ? { force: true } : {} }),
      onSuccess: inv,
    }),
  };
}

// ── Racks ──
export const useRacks = (stallId: string | undefined) =>
  useQuery({
    queryKey: ['racks', stallId],
    enabled: !!stallId,
    queryFn: () => apiGetPaged<Rack[]>(`/stalls/${stallId}/racks`, { limit: 100 }),
  });

export function useRackMutations(stallId?: string) {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ['racks'] });
  };
  return {
    create: useMutation({
      mutationFn: (b: Partial<Rack> & { generateQr?: boolean }) =>
        apiSend<Rack>('post', `/stalls/${stallId}/racks`, b),
      onSuccess: inv,
    }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Rack> }) => apiSend<Rack>('put', `/racks/${id}`, body), onSuccess: inv }),
    remove: useMutation({
      mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
        apiSend('delete', `/racks/${id}`, undefined, { params: force ? { force: true } : {} }),
      onSuccess: inv,
    }),
    generateQr: useMutation({
      mutationFn: ({ id, rotateToken }: { id: string; rotateToken?: boolean }) =>
        apiSend<{ qrToken: string; qrUrl: string }>('post', `/racks/${id}/generate-qr`, { rotateToken }),
      onSuccess: inv,
    }),
  };
}

// ── Products ──
export const useProducts = (params: Query) =>
  useQuery({
    queryKey: ['products', params],
    queryFn: () => apiGetPaged<Product[]>('/products', clean(params)),
  });

export const useProduct = (id: string | undefined) =>
  useQuery({
    queryKey: ['product', id],
    enabled: !!id,
    queryFn: () => apiGet<Product>(`/products/${id}`),
  });

export function useProductMutations() {
  const qc = useQueryClient();
  const inv = (id?: string) => {
    qc.invalidateQueries({ queryKey: ['products'] });
    if (id) qc.invalidateQueries({ queryKey: ['product', id] });
  };
  return {
    create: useMutation({ mutationFn: (b: Partial<Product>) => apiSend<Product>('post', '/products', b), onSuccess: () => inv() }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiSend<Product>('put', `/products/${id}`, body),
      onSuccess: (_d, v) => inv(v.id),
    }),
    remove: useMutation({
      mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
        apiSend('delete', `/products/${id}`, undefined, { params: force ? { force: true } : {} }),
      onSuccess: () => inv(),
    }),
    bulkDelete: useMutation({
      mutationFn: (b: { ids: string[]; force?: boolean }) =>
        apiSend<{ deleted: string[]; skipped: { id: string; reason: string }[] }>(
          'post',
          '/products/bulk-delete',
          b,
        ),
      onSuccess: () => inv(),
    }),
    addImages: useMutation({
      mutationFn: ({ id, files }: { id: string; files: File[] }) => {
        const fd = new FormData();
        files.forEach((f) => fd.append('images', f));
        return apiUpload<{ id: string; images: ProductImage[] }>(`/products/${id}/images`, fd);
      },
      onSuccess: (_d, v) => inv(v.id),
    }),
    setPrimaryImage: useMutation({
      mutationFn: ({ id, imageId }: { id: string; imageId: string }) => apiSend('patch', `/products/${id}/images/${imageId}/primary`),
      onSuccess: (_d, v) => inv(v.id),
    }),
    deleteImage: useMutation({
      mutationFn: ({ id, imageId }: { id: string; imageId: string }) => apiSend('delete', `/products/${id}/images/${imageId}`),
      onSuccess: (_d, v) => inv(v.id),
    }),
  };
}

// ── Assignments ──
export const useAssignments = (participationId: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['assignments', participationId, params],
    enabled: !!participationId,
    queryFn: () => apiGetPaged<Assignment[]>(`/participations/${participationId}/assignments`, clean({ limit: 50, ...params })),
  });

export function useAssignmentMutations(participationId?: string | null) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['assignments', participationId] });
  return {
    assign: useMutation({
      mutationFn: (b: { stallId: string; rackId: string; productIds: string[]; displayPrice?: string; availableQuantity?: number }) =>
        apiSend('post', `/participations/${participationId}/products/assign`, b),
      onSuccess: inv,
    }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Query }) => apiSend('put', `/expo-products/${id}`, body), onSuccess: inv }),
    move: useMutation({ mutationFn: ({ id, body }: { id: string; body: Query }) => apiSend('post', `/expo-products/${id}/move`, body), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => apiSend('delete', `/expo-products/${id}`), onSuccess: inv }),
  };
}

// ── Bookings ──
export const useBookings = (params: Query) =>
  useQuery({
    queryKey: ['bookings', params],
    queryFn: () => apiGetPaged<Booking[]>('/bookings', clean(params)),
  });

export const useBooking = (id: string | undefined) =>
  useQuery({ queryKey: ['booking', id], enabled: !!id, queryFn: () => apiGet<Booking>(`/bookings/${id}`) });

export function useBookingStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      apiSend<Booking>('put', `/bookings/${id}/status`, { status, note }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['booking', v.id] });
    },
  });
}

// ── Imports ──
export const useImports = () =>
  useQuery({
    queryKey: ['imports'],
    queryFn: () => apiGetPaged<ImportBatch[]>('/imports', { limit: 50 }),
  });
export const useImport = (id: string | undefined) =>
  useQuery({ queryKey: ['import', id], enabled: !!id, queryFn: () => apiGet<ImportBatch>(`/imports/${id}`) });

export const useImportRows = (id: string | undefined, params: Query = {}) =>
  useQuery({
    queryKey: ['import-rows', id, params],
    enabled: !!id,
    queryFn: () => apiGetPaged<ImportRow[]>(`/imports/${id}/rows`, clean({ limit: 50, ...params })),
  });

export type ImportUploadOptions = {
  selectSheets?: string[];
  columnMaps?: Record<string, Record<string, string>>;
};

export function useImportMutations() {
  const qc = useQueryClient();
  return {
    upload: useMutation({
      mutationFn: ({ file, options }: { file: File; options?: ImportUploadOptions }) => {
        const fd = new FormData();
        fd.append('file', file);
        if (options && Object.keys(options).length) fd.append('options', JSON.stringify(options));
        return apiUpload<ImportUploadResult>('/products/import', fd);
      },
    }),
    commit: useMutation({
      mutationFn: ({
        id,
        body,
      }: {
        id: string;
        body: { sheets?: string[]; duplicatePolicy: DuplicatePolicy; allowDuplicateBarcode: boolean };
      }) => apiSend<ImportCommitResult>('post', `/imports/${id}/commit`, body),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['products'] });
        qc.invalidateQueries({ queryKey: ['imports'] });
      },
    }),
  };
}

// ── Users ──
export const useUsers = (params: Query = {}) =>
  useQuery({
    queryKey: ['users', params],
    queryFn: () => apiGetPaged<AdminUser[]>('/users', clean({ limit: 100, ...params })),
  });

export function useUserMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['users'] });
  return {
    create: useMutation({
      mutationFn: (b: Record<string, unknown>) => apiSend<AdminUser>('post', '/users', b),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        apiSend<AdminUser>('put', `/users/${id}`, body),
      onSuccess: inv,
    }),
    resetPassword: useMutation({
      mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
        apiSend('post', `/users/${id}/reset-password`, { newPassword }),
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiSend('delete', `/users/${id}`),
      onSuccess: inv,
    }),
  };
}

// ── Audit ──
export const useAuditLogs = (params: Query) =>
  useQuery({
    queryKey: ['audit', params],
    queryFn: () => apiGetPaged<AuditLogEntry[]>('/audit-logs', clean(params)),
  });

// ── Visitors ──
export const useVisitors = (params: Query = {}) =>
  useQuery({
    queryKey: ['visitors', params],
    queryFn: () => apiGetPaged<Visitor[]>('/visitors', clean({ limit: 25, ...params })),
  });

export const useVisitor = (id: string | null) =>
  useQuery({
    queryKey: ['visitor', id],
    enabled: !!id,
    queryFn: () => apiGet<VisitorDetail>(`/visitors/${id}`),
  });

// ── QR Codes ──
export const useParticipationRacks = (participationId: string | null, params: Query = {}) =>
  useQuery({
    queryKey: ['participation-racks', participationId, params],
    enabled: !!participationId,
    queryFn: () => apiGetPaged<QrRack[]>(`/participations/${participationId}/racks`, clean(params)),
  });

// ── Reports ──
export const useReportMeta = () =>
  useQuery({
    queryKey: ['report-meta'],
    queryFn: () => apiGet<{ types: string[]; formats: string[] }>('/reports'),
  });

export const downloadReport = (type: string, format: string, params: Query) =>
  apiDownload(`/reports/${type}`, clean({ format, ...params }), `${type}.${format}`);

// ── Rack QR ──
export const downloadRackQr = (rackId: string, rackNumber: string) =>
  apiDownload(
    `/racks/${rackId}/qr`,
    { format: 'png', download: true, size: 720 },
    `QR-${rackNumber}.png`,
  );

// ── Account (settings) ──
export const useMe = () =>
  useQuery({ queryKey: ['me'], queryFn: () => apiGet<AdminUser>('/auth/me') });

export function useChangePassword() {
  return useMutation({
    mutationFn: (b: { currentPassword: string; newPassword: string }) =>
      apiSend('post', '/auth/change-password', b),
  });
}

export function useLogoutAll() {
  return useMutation({ mutationFn: () => apiSend('post', '/auth/logout-all') });
}
